import React, { useState } from 'react';
import styled from 'styled-components';
import { motion, AnimatePresence } from 'framer-motion';
import { FaChevronLeft, FaChevronRight, FaGithub, FaExternalLinkAlt, FaTimes } from 'react-icons/fa';
import { useInView } from 'react-intersection-observer';
import { device } from '../../styles/responsive';

const ProjectsSection = styled(motion.section)`
  min-height: 100vh;
  width: 100%;
  background-color: #000;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  position: relative;
  overflow: hidden;
  padding: 1rem;
  
  @media ${device.md} {
    padding: 60px 1rem;
    margin-top: 30px;
    margin-bottom: 30px;
  }
  
  @media ${device.sm} {
    padding: 40px 1rem;
    min-height: auto;
    margin-top: 60px;
    margin-bottom: 60px;
  }
`;

const ProjectsTitle = styled.h2`
  color: #ffffff;
  font-size: 2.5rem;
  font-weight: 600;
  margin-bottom: 3rem;
  text-align: center;
  position: relative;
  
  @media ${device.md} {
    font-size: 2rem;
    margin-bottom: 2.5rem;
  }
  
  @media ${device.sm} {
    font-size: 1.8rem;
    margin-bottom: 2rem;
  }
  
  &::after {
    content: '';
    position: absolute;
    bottom: -10px;
    left: 50%;
    transform: translateX(-50%);
    width: 60px;
    height: 3px;
    background: #00ffff;
    border-radius: 2px;
    
    @media ${device.sm} {
      width: 40px;
      height: 2px;
    }
  }
`;

const CarouselContainer = styled.div`
  width: 100%;
  max-width: 1600px;
  position: relative;
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 0 6rem;
  overflow: visible;
  
  @media ${device.lg} {
    padding: 0 4rem;
  }
  
  @media ${device.md} {
    padding: 0 3rem;
  }
  
  @media ${device.sm} {
    padding: 0 2rem;
  }
`;

const ProjectsCarousel = styled.div`
  position: relative;
  width: 100%;
  height: auto;
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 4rem 0;
  
  @media ${device.md} {
    padding: 3rem 0 4rem 0;
    min-height: 400px;
  }
  
  @media ${device.sm} {
    padding: 2rem 0 3rem 0;
    min-height: 350px;
  }
`;

const ProjectCard = styled(motion.div)`
  width: 100%;
  max-width: 1400px;
  height: auto;
  min-height: 400px;
  background: rgba(0, 0, 0, 0.5);
  border: 1px solid rgba(0, 255, 255, 0.1);
  border-radius: 20px;
  overflow: hidden;
  position: absolute;
  display: flex;
  flex-direction: row;
  backdrop-filter: blur(10px);
  
  @media ${device.md} {
    min-height: 350px;
    flex-direction: column;
    position: relative;
  }
  
  @media ${device.sm} {
    min-height: 300px;
    border-radius: 16px;
    flex-direction: column;
    position: relative;
  }
  
  &::before {
    content: '';
    position: absolute;
    inset: 0;
    background: radial-gradient(
      circle at top right,
      rgba(0, 255, 255, 0.1),
      transparent 70%
    );
    opacity: 0;
    transition: opacity 0.5s ease;
  }
  
  &:hover {
    border-color: rgba(0, 255, 255, 0.3);
    box-shadow: 
      0 15px 30px rgba(0, 0, 0, 0.4),
      0 0 20px rgba(0, 255, 255, 0.1);
      
    &::before {
      opacity: 1;
    }
  }
`;

const ProjectContent = styled.div`
  padding: 5rem;
  flex: 1;
  display: flex;
  flex-direction: column;
  justify-content: center;
  position: relative;
  z-index: 2;
  width: 50%;
  
  @media ${device.xl} {
    padding: 4rem;
  }
  
  @media ${device.lg} {
    padding: 3rem;
  }
  
  @media ${device.md} {
    padding: 2.5rem;
    width: 100%;
  }
  
  @media ${device.sm} {
    padding: 1.5rem;
    width: 100%;
  }
`;

const ProjectImage = styled.div`
  width: 50%;
  height: 100%;
  background-image: url(${props => props.image});
  background-size: cover;
  background-position: center;
  border-left: 1px solid rgba(0, 255, 255, 0.1);
  position: relative;
  
  @media ${device.md} {
    display: none;
  }
  
  @media ${device.sm} {
    display: none;
  }
`;

const ProjectTitle = styled.h3`
  color: #ffffff;
  font-size: 3.5rem;
  font-weight: 500;
  margin: 0 0 2.5rem 0;
  line-height: 1.2;
  
  @media ${device.xl} {
    font-size: 3rem;
    margin: 0 0 2rem 0;
  }
  
  @media ${device.lg} {
    font-size: 2.5rem;
    margin: 0 0 1.5rem 0;
  }
  
  @media ${device.md} {
    font-size: 2.2rem;
    margin: 0 0 1rem 0;
  }
  
  @media ${device.sm} {
    font-size: 1.8rem;
  }
`;

const ProjectDescription = styled.p`
  color: rgba(255, 255, 255, 0.8);
  font-size: 1.4rem;
  line-height: 1.6;
  margin: 0;
  flex-grow: 1;
  
  @media ${device.xl} {
    font-size: 1.3rem;
  }
  
  @media ${device.lg} {
    font-size: 1.2rem;
    line-height: 1.5;
  }
  
  @media ${device.md} {
    font-size: 1.1rem;
    line-height: 1.4;
  }
  
  @media ${device.sm} {
    font-size: 1rem;
    line-height: 1.3;
  }
`;

const LearnMoreButton = styled.button`
  background: transparent;
  border: 1.5px solid #00ffff;
  color: #00ffff;
  padding: 1rem 2.5rem;
  border-radius: 8px;
  font-size: 1.2rem;
  cursor: pointer;
  transition: all 0.3s ease;
  margin-top: 3rem;
  align-self: flex-start;
  position: relative;
  z-index: 2;
  
  @media ${device.lg} {
    padding: 0.8rem 2rem;
    font-size: 1.1rem;
    margin-top: 2rem;
  }
  
  @media ${device.md} {
    padding: 0.7rem 1.5rem;
    font-size: 1rem;
    margin-top: 1.5rem;
  }
  
  @media ${device.sm} {
    padding: 0.6rem 1.2rem;
    font-size: 0.9rem;
    margin-top: 1.2rem;
  }
  
  &:hover {
    background: rgba(0, 255, 255, 0.1);
    transform: translateY(-2px);
  }

  &:disabled {
    opacity: 0.5;
    cursor: not-allowed;
    transform: none;
  }
`;

const CarouselButton = styled.button`
  position: absolute;
  top: 50%;
  transform: translateY(-50%);
  background: rgba(0, 0, 0, 0.5);
  border: 1px solid rgba(255, 255, 255, 0.2);
  color: white;
  width: 50px;
  height: 50px;
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
  cursor: pointer;
  transition: all 0.3s ease;
  backdrop-filter: blur(5px);
  z-index: 10;
  
  @media ${device.md} {
    width: 40px;
    height: 40px;
  }
  
  @media ${device.sm} {
    width: 36px;
    height: 36px;
  }
  
  &:hover {
    background: rgba(0, 0, 0, 0.7);
    border-color: #00ffff;
    transform: translateY(-50%) scale(1.1);
  }
  
  &[data-position="left"] {
    left: 1rem;
  }
  
  &[data-position="right"] {
    right: 1rem;
  }
  
  svg {
    font-size: 1.2rem;
    
    @media ${device.sm} {
      font-size: 1rem;
    }
  }
`;

const ModalOverlay = styled(motion.div)`
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background: rgba(0, 0, 0, 0.8);
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 1000;
  backdrop-filter: blur(5px);
`;

const ModalContent = styled(motion.div)`
  background: rgba(0, 0, 0, 0.9);
  border: 1px solid rgba(0, 255, 255, 0.2);
  border-radius: 15px;
  padding: 2rem;
  max-width: 800px;
  width: 90%;
  position: relative;
  max-height: 90vh;
  overflow-y: auto;
  
  @media ${device.md} {
    padding: 1.5rem;
    width: 95%;
    max-height: 85vh;
  }
  
  @media ${device.sm} {
    padding: 1rem;
    border-radius: 12px;
    max-height: 80vh;
  }
  
  &::-webkit-scrollbar {
    width: 8px;
  }
  
  &::-webkit-scrollbar-track {
    background: rgba(255, 255, 255, 0.1);
    border-radius: 4px;
  }
  
  &::-webkit-scrollbar-thumb {
    background: rgba(0, 255, 255, 0.3);
    border-radius: 4px;
  }
`;

const ModalHeader = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: flex-start;
  margin-bottom: 1.5rem;
  
  @media ${device.sm} {
    margin-bottom: 1rem;
  }
`;

const ModalTitle = styled.h2`
  color: #ffffff;
  font-size: 2rem;
  margin: 0;
  
  @media ${device.md} {
    font-size: 1.8rem;
  }
  
  @media ${device.sm} {
    font-size: 1.5rem;
  }
`;

const CloseButton = styled.button`
  background: transparent;
  border: none;
  color: rgba(255, 255, 255, 0.6);
  cursor: pointer;
  padding: 0.5rem;
  transition: color 0.3s ease;
  
  &:hover {
    color: #00ffff;
  }
  
  svg {
    font-size: 1.5rem;
  }
`;

const ModalDescription = styled.div`
  color: rgba(255, 255, 255, 0.9);
  font-size: 1rem;
  line-height: 1.8;
  margin-bottom: 2rem;
`;

const TechStack = styled.div`
  margin-top: 1.5rem;
`;

const TechTitle = styled.h4`
  color: #00ffff;
  font-size: 1.1rem;
  margin: 0 0 1rem 0;
`;

const TechList = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: 0.8rem;
`;

const TechTag = styled.span`
  background: rgba(0, 255, 255, 0.1);
  border: 1px solid rgba(0, 255, 255, 0.2);
  color: rgba(255, 255, 255, 0.9);
  padding: 0.4rem 0.8rem;
  border-radius: 20px;
  font-size: 0.9rem;
  
  @media ${device.sm} {
    padding: 0.3rem 0.6rem;
    font-size: 0.8rem;
    border-radius: 16px;
  }
`;

const ProjectLinks = styled.div`
  display: flex;
  gap: 1rem;
  margin-top: 2rem;
`;

const ProjectLink = styled.a`
  display: flex;
  align-items: center;
  gap: 0.5rem;
  color: #00ffff;
  text-decoration: none;
  font-size: 0.9rem;
  padding: 0.5rem 1rem;
  border: 1px solid rgba(0, 255, 255, 0.3);
  border-radius: 5px;
  transition: all 0.3s ease;
  
  &:hover {
    background: rgba(0, 255, 255, 0.1);
    transform: translateY(-2px);
  }
  
  svg {
    font-size: 1rem;
  }
`;

const NoProjectsMessage = styled.div`
  color: rgba(255, 255, 255, 0.7);
  font-size: 1.2rem;
  text-align: center;
  width: 100%;
  padding: 2rem;
`;

const LoadingCircle = styled(motion.div)`
  width: 60px;
  height: 60px;
  border: 3px solid rgba(0, 255, 255, 0.1);
  border-top: 3px solid #00ffff;
  border-radius: 50%;
  margin: 2rem auto;
`;

const StatusBadge = styled.div`
  display: inline-flex;
  align-items: center;
  padding: 0.5rem 1rem;
  border-radius: 20px;
  font-size: 0.9rem;
  font-weight: 500;
  margin-bottom: 1.5rem;
  background: ${props => props.status === 'completed' ? 'rgba(0, 255, 0, 0.1)' : 'rgba(255, 165, 0, 0.1)'};
  color: ${props => props.status === 'completed' ? '#00ff00' : '#ffa500'};
  border: 1px solid ${props => props.status === 'completed' ? 'rgba(0, 255, 0, 0.3)' : 'rgba(255, 165, 0, 0.3)'};
  
  @media ${device.sm} {
    padding: 0.4rem 0.8rem;
    font-size: 0.8rem;
    margin-bottom: 1rem;
  }
  
  &::before {
    content: '';
    display: inline-block;
    width: 8px;
    height: 8px;
    border-radius: 50%;
    background: ${props => props.status === 'completed' ? '#00ff00' : '#ffa500'};
    margin-right: 8px;
  }
`;

const projectsData = [
  {
    id: 1,
    title: "AI-Powered Debugger",
    shortDescription: "An AI-driven debugger that provides real-time visualization, automated error detection, and intelligent recommendations.",
    image: "/project3.jpg",
    fullDescription: "Ender is a next-generation debugging tool that enhances debugging with AI-powered error detection, real-time execution path visualization, and dynamic state monitoring. It leverages the TinyLlama AI model fine-tuned with a new dataset to analyze logs, suggest fixes, and optimize performance.",
    techStack: ["Python", "C++", "Electron", "React", "Node.js", "PyTorch", "Hugging Face", "MongoDB", "Docker", 'Rest API', "WebSocket", "JSON"],
    "features": [
      "AI-powered error detection and analysis",
      "Real-time execution path visualization",
      "Dynamic variable state monitoring",
      "Multi-language support including Linux scripts",
      "Automated recommendations for bug fixes",
      "IDE integration as a plugin/extension"
    ],
    github: "https://github.com/yourusername/ml-portfolio",
    category: "ai-ml",
    status: "in-progress"
  },
  {
    id: 2,
    title: "FeelTok - Social Media App",
    shortDescription: "A real-time social media app leveraging emotional exepreince to connect people.",
    image: "/project1.jpg",
    fullDescription: "A mobile application and admin panel platform designed to reconnect, rekindle, and enhance human relationships. The app is centered around six core emotional tenets: Love, Gratitude, Apology, Appreciation, Mindfulness, and Thank You. Using Agile project management techniques, Feeltok aims to foster meaningful connections and positive relationships.",
    techStack: ['React Native', 'PostgreSQL', 'Supabase', 'Node.js', "HTML/CSS", "Javascript"],
    features: [
      "User authentication with email and third-party login",
      "Profile creation with custom usernames and images",
      "Secure two-factor authentication (2FA)",
      "Post creation with text and image support",
      "Category-based post organization",
      "Supabase-powered backend with PostgreSQL database",
      "React Native mobile app with a seamless UI"
    ],
    github: "https://github.com/MQZakir/FeelTok-Social-Media-App",
    category: "applications",
    status: "completed"
  },
  {
    id: 3,
    title: "Horologica - E-commerce App",
    shortDescription: "Online desktop application for luxury and premium swiss watches",
    image: "/project2.jpg",
    fullDescription: "HoroLogica is an online retailer for luxurios and premium swiss watches. This retailer had a vision in mind, to create a shopping experience for watches where the location or visiting a store isnt necessary",
    techStack: [".NET", "C#", "SQL", "WPF", "XML", "Microsoft SSMS"],
    features: [
      "Curated product catalog with gender-based collections",
      "Advanced search with real-time filtering",
      "Persistent shopping bag with stock validation",
      "Secure user authentication and profile management",
      "Dynamic UI with seamless navigation",
      "SQL-integrated backend for data management",
      "WPF-driven modern interface with sleek aesthetics"
    ],
    github: "https://github.com/MQZakir/Horologica_E-commerce_App",
    category: "applications",
    status: "completed"
  },
];

const containerVariants = {
  hidden: { 
    opacity: 0,
    scale: 0.98
  },
  visible: { 
    opacity: 1,
    scale: 1,
    transition: {
      duration: 0.8,
      ease: "easeOut",
      staggerChildren: 0.15
    }
  },
  exit: {
    opacity: 0,
    scale: 0.98,
    transition: {
      duration: 0.6,
      ease: "easeIn",
      staggerChildren: 0.1,
      staggerDirection: -1
    }
  }
};

const Projects = () => {
  const [[currentIndex, direction], setCurrentIndex] = useState([0, 0]);
  const [selectedProject, setSelectedProject] = useState(null);
  const [isTransitioning, setIsTransitioning] = useState(false);
  const [ref, inView] = useInView({
    threshold: 0.2,
    triggerOnce: false
  });
  
  const slideVariants = {
    enter: (direction) => ({
      x: direction > 0 ? 1000 : -1000,
      opacity: 0
    }),
    center: {
      zIndex: 1,
      x: 0,
      opacity: 1
    },
    exit: (direction) => ({
      zIndex: 0,
      x: direction < 0 ? 1000 : -1000,
      opacity: 0
    })
  };

  const swipeConfidenceThreshold = 10000;
  const swipePower = (offset, velocity) => {
    return Math.abs(offset) * velocity;
  };
  
  const paginate = (newDirection) => {
    if (isTransitioning) return;
    setCurrentIndex(([current, _]) => {
      const nextIndex = (current + newDirection + projectsData.length) % projectsData.length;
      return [nextIndex, newDirection];
    });
  };

  const handleProjectSelect = (project) => {
    if (isTransitioning) return;
    setSelectedProject(project);
  };

  return (
    <ProjectsSection
      ref={ref}
      id="projects"
      initial="hidden"
      animate={inView ? "visible" : "hidden"}
      exit="exit"
      variants={containerVariants}
    >
      <CarouselContainer
        initial={{ opacity: 0, y: 30 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.6, delay: 0.4 }}
      >
        <CarouselButton 
          data-position="left"
          onClick={() => paginate(-1)}
          disabled={isTransitioning}
        >
          <FaChevronLeft />
        </CarouselButton>
        
        <ProjectsCarousel>
          <AnimatePresence initial={false} custom={direction} mode="wait">
            <ProjectCard
              key={currentIndex}
              custom={direction}
              variants={slideVariants}
              initial="enter"
              animate="center"
              exit="exit"
              transition={{
                x: { type: "spring", stiffness: 300, damping: 30 },
                opacity: { duration: 0.2 }
              }}
              drag="x"
              dragConstraints={{ left: 0, right: 0 }}
              dragElastic={1}
              onDragEnd={(e, { offset, velocity }) => {
                if (isTransitioning) return;
                const swipe = swipePower(offset.x, velocity.x);

                if (swipe < -swipeConfidenceThreshold) {
                  paginate(1);
                } else if (swipe > swipeConfidenceThreshold) {
                  paginate(-1);
                }
              }}
            >
              <ProjectContent>
                <StatusBadge status={projectsData[currentIndex].status}>
                  {projectsData[currentIndex].status === 'completed' ? 'Completed' : 'In Progress'}
                </StatusBadge>
                <ProjectTitle>{projectsData[currentIndex].title}</ProjectTitle>
                <ProjectDescription>
                  {projectsData[currentIndex].shortDescription}
                </ProjectDescription>
                <LearnMoreButton 
                  onClick={() => handleProjectSelect(projectsData[currentIndex])}
                  disabled={isTransitioning}
                >
                  Learn More
                </LearnMoreButton>
              </ProjectContent>
              <ProjectImage image={projectsData[currentIndex].image} />
            </ProjectCard>
          </AnimatePresence>
        </ProjectsCarousel>
        
        <CarouselButton 
          data-position="right"
          onClick={() => paginate(1)}
          disabled={isTransitioning}
        >
          <FaChevronRight />
        </CarouselButton>
      </CarouselContainer>
      
      <AnimatePresence>
        {selectedProject && !isTransitioning && (
          <ModalOverlay
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            onClick={() => setSelectedProject(null)}
          >
            <ModalContent
              initial={{ scale: 0.8, opacity: 0 }}
              animate={{ scale: 1, opacity: 1 }}
              exit={{ scale: 0.8, opacity: 0 }}
              onClick={(e) => e.stopPropagation()}
            >
              <ModalHeader>
                <ModalTitle>{selectedProject.title}</ModalTitle>
                <CloseButton onClick={() => setSelectedProject(null)}>
                  <FaTimes />
                </CloseButton>
              </ModalHeader>
              
              <ModalDescription>
                {selectedProject.fullDescription}
                
                <div style={{ marginTop: '1.5rem' }}>
                  <h4 style={{ color: '#00ffff', marginBottom: '0.8rem' }}>Key Features:</h4>
                  <ul style={{ color: 'rgba(255, 255, 255, 0.9)', paddingLeft: '1.2rem' }}>
                    {selectedProject.features.map((feature, index) => (
                      <li key={index} style={{ marginBottom: '0.5rem' }}>{feature}</li>
                    ))}
                  </ul>
                </div>
                
                <TechStack>
                  <TechTitle>Technologies Used</TechTitle>
                  <TechList>
                    {selectedProject.techStack.map((tech, index) => (
                      <TechTag key={index}>{tech}</TechTag>
                    ))}
                  </TechList>
                </TechStack>
                
                <ProjectLinks>
                  <ProjectLink href={selectedProject.github} target="_blank" rel="noopener noreferrer">
                    <FaGithub /> View Source
                  </ProjectLink>
                </ProjectLinks>
              </ModalDescription>
            </ModalContent>
          </ModalOverlay>
        )}
      </AnimatePresence>
    </ProjectsSection>
  );
};

export default Projects;